import { ReactNode, useContext, useEffect } from 'react';

import { Field, WrappedFieldProps } from 'redux-form';
import { CommanderFormContext } from '@mews-commander/ui';
import { Input, InputProps, InputValue, Field as OptimusField, Size } from '@optimus-web/core';
import { getError } from '../../utils';

interface NumberInputFieldProps {
    min?: number;
    max?: number;
    size?: Size;
    name: string;
    suffix?: React.ReactNode;
    alignRight?: boolean;
    disabled?: boolean;
    label?: ReactNode;
}

type HandleInputValueChange = (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, newValue: number) => void;

type OnChangeInputValue = (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, newValue: InputValue) => void;

interface NumberInputProps extends NumberInputFieldProps {
    onChange: OnChangeInputValue;
    size?: Size;
    value: string;
    changeValueWhenOverMinMaxLimit: (value: InputValue) => void;
}

const NumberInput = (props: NumberInputProps) => {
    const {
        max = Number.MAX_SAFE_INTEGER,
        min = Number.MIN_SAFE_INTEGER,
        onChange,
        size,
        value,
        changeValueWhenOverMinMaxLimit,
    } = props;

    useEffect(() => {
        const parsedValue = parseInt(value, 10);
        if (!Number.isNaN(parsedValue)) {
            if (parsedValue > max) {
                changeValueWhenOverMinMaxLimit(max.toString());
            }
            if (parsedValue < min) {
                changeValueWhenOverMinMaxLimit(min.toString());
            }
        }
    }, [value, min, max, changeValueWhenOverMinMaxLimit]);

    const handleValueChange: HandleInputValueChange = (e, newValue) => {
        const boundedValue = Math.max(min, Math.min(newValue, max));
        onChange(e, boundedValue.toString());
    };

    const handleChangeEvent = (e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, inputValue: InputValue) => {
        if (inputValue === '') {
            onChange(e, inputValue);
        } else {
            const parsedValue = parseInt(inputValue as string, 10);
            const newValue = Number.isNaN(parsedValue) ? 0 : parsedValue;
            handleValueChange(e, newValue);
        }
    };

    const inputProps: InputProps = {
        ...props,
        onChange: handleChangeEvent,
        type: 'number',
        size,
    };

    return <Input {...inputProps} />;
};

const OptimusFieldNumberInput = OptimusField(NumberInput);

const NumberInputFieldRenderer = ({ input, meta, disabled, ...other }: WrappedFieldProps & NumberInputFieldProps) => {
    const { onChange } = input;
    const handleChange = (event: React.SyntheticEvent, value: InputValue) => onChange(value);
    const changeValueWhenOverMinMaxLimit = (value: InputValue) => onChange(value);
    const { readOnlyForm } = useContext(CommanderFormContext);

    return (
        <OptimusFieldNumberInput
            disabled={readOnlyForm || disabled}
            error={getError(meta)}
            {...input}
            {...other}
            onChange={handleChange}
            changeValueWhenOverMinMaxLimit={changeValueWhenOverMinMaxLimit}
        />
    );
};

const NumberInputField = (props: NumberInputFieldProps) => <Field component={NumberInputFieldRenderer} {...props} />;

export default NumberInputField;
